import { z } from 'zod';
import * as dt from '../data.types.js';
import {
  BaseResponseSchema,
  containerSizeSchema,
  containerStatusSchema,
  containerTypeSchema,
  dateSchema,
  integerIdSchema,
  nullableIntegerIdSchema,
  optionalQueryBooleanSchema,
  waferFingerprintSchema,
  waferMaterialTypeSchema,
  waferPatternTypeSchema,
  waferSizeSchema,
  waferStatusSchema,
  waferThicknessTypeSchema,
} from './primitives.js';
import { FileMetadataSchema } from './files.schemas.js';
import { containerBarcodeSchema } from '../efem-barcode.schemas.js';
import { ContainerResponseSchema, WaferResponseSchema } from './tenant-entity-responses.schemas.js';
export const CreateWaferRequestBaseSchema = z.object({
  type: waferPatternTypeSchema,
  size: waferSizeSchema,
  layerName: z.string().min(1).max(100).trim(),
  companyAlias: z.string().min(1).max(50).trim(),
  ownerContactVersionId: z.string().min(1),
  receivedDate: dateSchema,
  material: waferMaterialTypeSchema.optional(),
  thickness: waferThicknessTypeSchema.optional(),
  companyName: z.string().max(100).trim().optional(),
  groupIds: z.array(integerIdSchema).optional(),
  returnedDate: dateSchema.optional(),
  status: waferStatusSchema.optional(),
  shippedDate: dateSchema.optional(),
  images: z.array(FileMetadataSchema).optional(),
  files: z.array(FileMetadataSchema).optional(),
  fingerprint: waferFingerprintSchema.optional().nullable(),
  fingerprintImages: z.array(FileMetadataSchema).optional().nullable(),
  containerId: integerIdSchema.optional(),
  slotNumber: z.number().int().min(1).optional(),
  lotId: z.string().optional(),
  /** When set, persist EFEM learned lighting from this batch job slot after wafer create (auto-accept). */
  efemLearnedLightingJobId: z.string().min(1).optional(),
  efemLearnedLightingSlotNumber: z.number().int().min(1).optional(),
});

export const CreateWaferRequestSchema = CreateWaferRequestBaseSchema;

export const UpdateWaferRequestSchema = z.object({
  type: waferPatternTypeSchema.optional(),
  size: waferSizeSchema.optional(),
  layerName: z.string().min(1).max(100).trim().optional(),
  companyAlias: z.string().min(1).max(50).trim().optional(),
  ownerContactVersionId: z.string().min(1).optional(),
  receivedDate: dateSchema.optional(),
  material: waferMaterialTypeSchema.optional(),
  thickness: waferThicknessTypeSchema.optional(),
  companyName: z.string().max(100).trim().optional(),
  groupIds: z.array(integerIdSchema).optional(),
  returnedDate: dateSchema.optional(),
  status: waferStatusSchema.optional(),
  shippedDate: dateSchema.optional(),
  images: z.array(FileMetadataSchema).optional(),
  files: z.array(FileMetadataSchema).optional(),
  fingerprint: waferFingerprintSchema.optional().nullable(),
  fingerprintImages: z.array(FileMetadataSchema).optional(),
  containerId: nullableIntegerIdSchema.optional(),
  slotNumber: z.number().int().min(1).nullable().optional(),
  lotId: z.string().optional(),
  /** Required when container or slot assignment changes; stored on wafer_transfer_history. */
  locationChangeNotes: z.string().min(1).max(1000).trim().optional(),
});

export const GetWafersRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  type: waferPatternTypeSchema.optional(),
  size: waferSizeSchema.optional(),
  material: waferMaterialTypeSchema.optional(),
  status: waferStatusSchema.optional(),
  companyAlias: z.string().max(50).trim().optional(),
  group: z.string().max(100).trim().optional(),
  containerId: integerIdSchema.optional(),
  hasFingerprint: optionalQueryBooleanSchema,
  sortBy: z.enum(['id', 'layerName', 'companyAlias', 'receivedDate', 'status', 'createdAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});

/** Lightweight wafer row for API pickers / slot comparison (no file metadata). */
export const WaferPickerSummarySchema = z.object({
  id: integerIdSchema,
  layerName: z.string(),
  slotNumber: z.number().int().min(1).nullable().optional(),
  containerId: nullableIntegerIdSchema.optional(),
  fingerprint: waferFingerprintSchema.optional().nullable(),
  status: waferStatusSchema,
  type: waferPatternTypeSchema,
  size: waferSizeSchema,
});

export const GetWaferSummariesRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  containerId: integerIdSchema.optional(),
  /** When set, only wafers with one of these statuses are returned. */
  statuses: z.array(waferStatusSchema).optional(),
});

export const GetWaferSummariesResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    wafers: z.array(WaferPickerSummarySchema),
  }),
});

// Container Management Schemas
export const CreateContainerRequestBaseSchema = z.object({
  type: containerTypeSchema,
  containerName: z.string().min(1).max(100).trim(),
  containerBarcode: containerBarcodeSchema.optional(),
  size: containerSizeSchema,
  companyAlias: z.string().min(1).max(50).trim(),
  ownerContactVersionId: z.string().min(1),
  receivedDate: dateSchema,
  companyName: z.string().max(100).trim().optional(),
  groupIds: z.array(integerIdSchema).optional(),
  returnedDate: dateSchema.optional(),
  status: containerStatusSchema.optional(),
  shippedDate: dateSchema.optional(),
  images: z.array(FileMetadataSchema).optional(),
  files: z.array(FileMetadataSchema).optional(),
  beaconId: z.string().optional(),
  beaconVersion: z.number().int().optional()
});

export const CreateContainerRequestSchema = CreateContainerRequestBaseSchema;

export const UpdateContainerRequestSchema = z.object({
  type: containerTypeSchema.optional(),
  containerName: z.string().min(1).max(100).trim().optional(),
  containerBarcode: containerBarcodeSchema.nullable().optional(),
  size: containerSizeSchema.optional(),
  companyAlias: z.string().min(1).max(50).trim().optional(),
  ownerContactVersionId: z.string().min(1).optional(),
  receivedDate: dateSchema.optional(),
  companyName: z.string().max(100).trim().optional(),
  groupIds: z.array(integerIdSchema).optional(),
  returnedDate: dateSchema.optional(),
  status: containerStatusSchema.optional(),
  shippedDate: dateSchema.optional(),
  images: z.array(FileMetadataSchema).optional(),
  files: z.array(FileMetadataSchema).optional(),
  // null means "remove the beacon assignment"; undefined means "don't touch beaconId"
  beaconId: z.string().nullable().optional(),
  beaconVersion: z.number().int().optional()
});

export const GetContainersRequestSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
  type: containerTypeSchema.optional(),
  size: containerSizeSchema.optional(),
  status: containerStatusSchema.optional(),
  companyAlias: z.string().max(50).trim().optional(),
  group: z.string().max(100).trim().optional(),
  hasBeacon: optionalQueryBooleanSchema,
  isEmpty: optionalQueryBooleanSchema,
  /** When false, only containers without a CNP label (null/empty barcode). */
  hasBarcode: optionalQueryBooleanSchema,
  sortBy: z.enum(['id', 'companyAlias', 'receivedDate', 'status', 'waferCount', 'createdAt']).optional(),
  sortOrder: z.enum(['ASC', 'DESC']).optional()
});

/** Lightweight container row for pickers (EFEM, slot binding, etc.). */
export const ContainerSummarySchema = z.object({
  id: integerIdSchema,
  containerName: z.string(),
  containerBarcode: z.string().nullable().optional(),
  type: containerTypeSchema,
  size: containerSizeSchema,
  status: containerStatusSchema,
  waferCount: z.number().int().nonnegative(),
});

export const GetContainerSummariesRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  search: z.string().max(200).trim().optional(),
});

export const GetContainerSummariesResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    containers: z.array(ContainerSummarySchema),
  }),
});

/** Next unique default container name: Container_<MON>_<DD>_<YYYY>_<n> */
export const SuggestContainerNameResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    containerName: z.string().min(1),
  }),
});

export const UpdateWaferFingerprintRequestSchema = z.object({
  fingerprint: waferFingerprintSchema.optional(),
  fingerprintImages: z.array(FileMetadataSchema).optional()
});

// Batch Fingerprint Validation Schemas
export const ValidateFingerprintsRequestSchema = z.object({
  fingerprints: z.array(waferFingerprintSchema).min(1).max(100), // Support up to 100 fingerprints
  /** When editing a wafer, ignore its current fingerprint as a duplicate. */
  excludeWaferId: integerIdSchema.optional(),
});

export const ValidateFingerprintsResponseSchema = z.object({
  existingFingerprints: z.array(waferFingerprintSchema),
  duplicatesInBatch: z.array(waferFingerprintSchema)
});

export const CheckContainerBarcodeQuerySchema = z.object({
  barcode: z.string().min(1).max(250).trim(),
  /** When editing a container, ignore its current barcode as a duplicate. */
  excludeContainerId: integerIdSchema.optional(),
});

export const CheckContainerBarcodeResponseSchema = z.object({
  available: z.boolean(),
  message: z.string().optional(),
});

export const FileDeleteRequestSchema = z
  .object({
    objectKey: z.string().min(1).max(250).trim().optional(),
    objectKeys: z.array(z.string().min(1).max(250).trim()).optional(),
    fileType: z.nativeEnum(dt.FILE_CATEGORY),
  })
  .refine((data) => Boolean(data.objectKey) || (data.objectKeys?.length ?? 0) > 0, {
    message: 'objectKey or objectKeys is required',
  });

/** Fingerprint image row returned by GET /tenant/files/wafers/:waferId/image-history */
export const WaferFingerprintHistoryImageSchema = FileMetadataSchema.extend({
  fileId: integerIdSchema,
  entityType: z.string().optional(),
  entityId: integerIdSchema.optional().nullable(),
  fileCategory: z.string().optional(),
  uploadedByUserId: integerIdSchema.optional().nullable(),
  createdAt: dateSchema.optional(),
});



/**
 * Response for wafer fingerprint image history
 */
export const WaferImageHistoryResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    waferId: integerIdSchema,
    waferFingerprint: waferFingerprintSchema.optional(),
    images: z.array(WaferFingerprintHistoryImageSchema),
  }).optional(),
});

// Wafer Transfer Schemas
export const WaferTransferRequestSchema = z.object({
  id: integerIdSchema,
  targetContainerId: integerIdSchema,
  targetSlotNumber: z.number().int().min(1),
  comments: z.string().max(1000).optional()
});

// Batch Container with Wafers Creation Schema
export const CreateContainerWithWafersRequestSchema = z.object({
  container: CreateContainerRequestSchema,
  wafers: z.array(CreateWaferRequestBaseSchema.omit({ containerId: true })).min(1).max(100) // Max 100 wafers per batch
});

export const CreateContainerWithWafersResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    container: ContainerResponseSchema,
    wafers: z.array(WaferResponseSchema),
    createdWaferCount: z.number().int().min(0)
  })
});
// Inferred types
export type CheckContainerBarcodeQuery = z.infer<typeof CheckContainerBarcodeQuerySchema>;
export type CheckContainerBarcodeResponse = z.infer<typeof CheckContainerBarcodeResponseSchema>;
export type ContainerSummary = z.infer<typeof ContainerSummarySchema>;
export type CreateContainerRequest = z.infer<typeof CreateContainerRequestSchema>;
export type CreateContainerRequestBase = z.infer<typeof CreateContainerRequestBaseSchema>;
export type CreateContainerWithWafersRequest = z.infer<typeof CreateContainerWithWafersRequestSchema>;
export type CreateContainerWithWafersResponse = z.infer<typeof CreateContainerWithWafersResponseSchema>;
export type CreateWaferRequest = z.infer<typeof CreateWaferRequestSchema>;
export type CreateWaferRequestBase = z.infer<typeof CreateWaferRequestBaseSchema>;
export type FileDeleteRequest = z.infer<typeof FileDeleteRequestSchema>;
export type GetContainerSummariesRequest = z.infer<typeof GetContainerSummariesRequestSchema>;
export type GetContainerSummariesResponse = z.infer<typeof GetContainerSummariesResponseSchema>;
export type GetContainerSummariesResponseData = z.infer<typeof GetContainerSummariesResponseSchema.shape.data>;
export type GetContainersRequest = z.infer<typeof GetContainersRequestSchema>;
export type GetWaferSummariesRequest = z.infer<typeof GetWaferSummariesRequestSchema>;
export type GetWaferSummariesResponse = z.infer<typeof GetWaferSummariesResponseSchema>;
export type GetWaferSummariesResponseData = z.infer<typeof GetWaferSummariesResponseSchema.shape.data>;
export type GetWafersRequest = z.infer<typeof GetWafersRequestSchema>;
export type SuggestContainerNameResponse = z.infer<typeof SuggestContainerNameResponseSchema>;
export type SuggestContainerNameResponseData = z.infer<typeof SuggestContainerNameResponseSchema.shape.data>;
export type UpdateContainerRequest = z.infer<typeof UpdateContainerRequestSchema>;
export type UpdateWaferFingerprintRequest = z.infer<typeof UpdateWaferFingerprintRequestSchema>;
export type UpdateWaferRequest = z.infer<typeof UpdateWaferRequestSchema>;
export type ValidateFingerprintsRequest = z.infer<typeof ValidateFingerprintsRequestSchema>;
export type ValidateFingerprintsResponse = z.infer<typeof ValidateFingerprintsResponseSchema>;
export type WaferFingerprintHistoryImage = z.infer<typeof WaferFingerprintHistoryImageSchema>;
export type WaferImageHistoryResponse = z.infer<typeof WaferImageHistoryResponseSchema>;
export type WaferPickerSummary = z.infer<typeof WaferPickerSummarySchema>;
export type WaferTransferRequest = z.infer<typeof WaferTransferRequestSchema>;
